/**
 * Maison KALARA — configurateur « Vos Créations »
 *
 * Pilote la navigation en 8 étapes, la validation étape par étape, le
 * récapitulatif final et l'agrégation des choix multiples.
 *
 * Sans JavaScript, toutes les étapes restent visibles et le formulaire
 * fonctionne comme un formulaire long classique : l'attribut `hidden` n'est
 * retiré/posé que par ce script.
 */
class KalaraConfigurator extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;

    this.steps = Array.from(this.querySelectorAll('[data-step]'));
    if (this.steps.length === 0) return;

    this.progressItems = Array.from(this.querySelectorAll('[data-progress-item]'));
    this.progressButtons = Array.from(this.querySelectorAll('[data-progress-go]'));
    this.prevButton = this.querySelector('[data-prev]');
    this.nextButton = this.querySelector('[data-next]');
    this.submitButton = this.querySelector('[data-submit]');
    this.validationMessage = this.querySelector('[data-validation]');
    this.recapList = this.querySelector('[data-recap]');
    this.bodyTarget = this.querySelector('[data-body-target]');

    this.current = 0;
    // Étape la plus avancée déjà atteinte : borne la navigation par les numéros.
    this.maxStep = 0;

    this.prevButton?.addEventListener('click', () => this.goTo(this.current - 1));
    this.nextButton?.addEventListener('click', () => this.next());
    this.form.addEventListener('submit', (event) => this.onSubmit(event));

    // Les numéros du fil d'Ariane permettent de revenir sur une étape déjà
    // atteinte — et d'y ré-avancer — sans jamais sauter une étape non validée.
    this.progressButtons.forEach((button) => {
      button.addEventListener('click', () => this.onProgressClick(Number(button.dataset.progressGo) - 1));
    });

    // Un choix efface le message d'erreur : ne pas laisser une alerte
    // contredire ce que la cliente vient de corriger.
    this.form.addEventListener('change', (event) => {
      this.clearValidation();
      if (event.target.matches('[data-multi-source]')) this.syncMultiGroup(event.target.dataset.multiSource);
      if (event.target.type === 'file') this.previewFile(event.target);
    });

    // Pas d'enchaînement automatique après un choix : la cliente reste sur
    // l'étape le temps d'ajouter, si elle le souhaite, un commentaire. On avance
    // avec « Continuer » ou les numéros du fil d'Ariane.

    this.render();
  }

  /** Champs de l'étape courante qui participent à la validation native. */
  fieldsOf(index) {
    return Array.from(this.steps[index].querySelectorAll('input, select, textarea'));
  }

  next() {
    if (!this.validateStep(this.current)) return;
    if (this.current < this.steps.length - 1) this.goTo(this.current + 1);
  }

  goTo(index) {
    if (index < 0 || index > this.steps.length - 1) return;
    this.current = index;
    if (index > this.maxStep) this.maxStep = index;
    this.clearValidation();
    this.render();
    this.playEnter();

    // Ramener la vue en haut du formulaire, puis donner le focus à la légende
    // pour que les lecteurs d'écran annoncent la nouvelle étape.
    const top = this.getBoundingClientRect().top + window.scrollY - 24;
    window.scrollTo({ top, behavior: this.prefersReducedMotion ? 'auto' : 'smooth' });

    const legend = this.steps[index].querySelector('.kalara-config__legend');
    if (legend) {
      legend.setAttribute('tabindex', '-1');
      legend.focus({ preventScroll: true });
    }
  }

  /**
   * Navigation par les numéros du fil d'Ariane. Le retour en arrière est
   * toujours autorisé ; avancer n'est possible que vers une étape déjà atteinte,
   * et en revalidant les étapes traversées — jamais de court-circuit d'un champ
   * obligatoire.
   */
  onProgressClick(target) {
    if (Number.isNaN(target) || target === this.current) return;

    if (target < this.current) {
      this.goTo(target);
      return;
    }

    if (target > this.maxStep) return;

    for (let i = this.current; i < target; i += 1) {
      if (!this.validateStep(i)) {
        this.goTo(i);
        return;
      }
    }
    this.goTo(target);
  }

  get prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /**
   * Rejoue l'animation d'entrée sur l'étape qui vient d'apparaître, pour un
   * passage en douceur plutôt qu'un remplacement sec. Retirer puis reposer la
   * classe, avec un reflow entre les deux, force le navigateur à relancer
   * l'animation à chaque changement d'étape. Silencieux si la cliente a
   * demandé moins de mouvement.
   */
  playEnter() {
    if (this.prefersReducedMotion) return;
    const step = this.steps[this.current];
    if (!step) return;
    step.classList.remove('is-entering');
    void step.offsetWidth;
    step.classList.add('is-entering');
  }

  render() {
    this.steps.forEach((step, i) => {
      step.hidden = i !== this.current;
    });

    this.progressItems.forEach((item, i) => {
      item.classList.toggle('is-current', i === this.current);
      item.classList.toggle('is-done', i < this.current);
      if (i === this.current) {
        item.setAttribute('aria-current', 'step');
      } else {
        item.removeAttribute('aria-current');
      }
    });

    // Un numéro n'est cliquable qu'une fois son étape atteinte.
    this.progressButtons.forEach((button, i) => {
      button.disabled = i > this.maxStep;
    });

    const isLast = this.current === this.steps.length - 1;
    if (this.prevButton) this.prevButton.hidden = this.current === 0;
    if (this.nextButton) this.nextButton.hidden = isLast;
    if (this.submitButton) this.submitButton.hidden = !isLast;

    if (isLast) this.buildRecap();
  }

  /**
   * Valide l'étape courante avec la validation native du navigateur, en
   * n'inspectant que ses propres champs — sinon les champs masqués des étapes
   * suivantes feraient échouer la vérification.
   */
  validateStep(index) {
    const fields = this.fieldsOf(index);
    const invalid = fields.find((field) => !field.checkValidity());

    if (!invalid) return true;

    this.showValidation(this.messageFor(invalid, index));
    invalid.focus({ preventScroll: false });
    return false;
  }

  /**
   * Message d'erreur en français.
   *
   * `field.validationMessage` suit la langue de l'interface du navigateur, pas
   * celle de la page : une cliente sur un navigateur anglophone lirait
   * « Please select one of these options ». On rédige donc nos propres textes.
   */
  messageFor(field, stepIndex) {
    if (field.hasAttribute('data-consent')) {
      return "Merci d'accepter la politique de confidentialité pour envoyer votre demande.";
    }

    // Les boutons radio partagent un nom : nommer le groupe, pas le premier bouton.
    if (field.type === 'radio') {
      const groupTitle = field
        .closest('.kalara-config__group')
        ?.querySelector('.kalara-config__group-title')
        ?.firstChild?.textContent?.trim();
      const stepTitle = this.steps[stepIndex]
        .querySelector('.kalara-config__step-title')
        ?.textContent?.trim();
      return `Merci de faire un choix — ${groupTitle || stepTitle || 'cette étape'}`;
    }

    const label = field.labels?.[0]?.textContent?.trim().replace(/\s*\(cm\)$/, '') || 'ce champ';

    if (field.validity.valueMissing) return `Merci de renseigner : ${label}`;
    if (field.validity.typeMismatch && field.type === 'email') {
      return 'Cette adresse e-mail semble incomplète. Merci de la vérifier.';
    }
    if (field.validity.rangeUnderflow || field.validity.rangeOverflow) {
      return `${label} : indiquez une valeur entre ${field.min} et ${field.max} cm.`;
    }
    if (field.validity.stepMismatch) {
      return `${label} : arrondissez au demi-centimètre (par exemple 92,5).`;
    }

    return `Merci de vérifier : ${label}`;
  }

  showValidation(message) {
    if (!this.validationMessage) return;
    this.validationMessage.textContent = message;
    this.validationMessage.hidden = false;
  }

  clearValidation() {
    if (!this.validationMessage) return;
    this.validationMessage.hidden = true;
    this.validationMessage.textContent = '';
  }

  /**
   * Aperçu immédiat d'un fichier joint : vignette pour une image, nom du fichier
   * sinon. Rassure la cliente sur ce qui partira vraiment à l'atelier.
   */
  previewFile(input) {
    const field = input.closest('.kalara-config__upload') || input.parentElement;
    if (!field) return;

    let holder = field.querySelector('[data-file-preview]');
    if (!holder) {
      holder = document.createElement('div');
      holder.className = 'kalara-config__preview';
      holder.setAttribute('data-file-preview', '');
      field.append(holder);
    }
    holder.innerHTML = '';

    const file = input.files && input.files[0];
    const dropzone = field.querySelector('.kalara-config__dropzone');
    const cta = field.querySelector('[data-cta]');
    if (dropzone) dropzone.classList.toggle('has-file', !!file);
    if (cta) cta.textContent = file ? 'Changer le fichier' : 'Ajouter une image';
    if (!file) return;

    if (file.type.indexOf('image/') === 0) {
      const img = document.createElement('img');
      img.className = 'kalara-config__preview-img';
      img.alt = file.name;
      img.src = URL.createObjectURL(file);
      img.addEventListener('load', () => URL.revokeObjectURL(img.src), { once: true });
      holder.append(img);
    } else {
      const name = document.createElement('span');
      name.className = 'kalara-config__preview-name';
      name.textContent = file.name;
      holder.append(name);
    }
  }

  /**
   * Recopie les cases cochées d'un groupe dans son champ caché, sous forme
   * « Perles, Broderies ». Sans cela, plusieurs champs de même nom se
   * écraseraient mutuellement à l'envoi.
   */
  syncMultiGroup(groupName) {
    const target = this.form.querySelector(`[data-multi-target="${CSS.escape(groupName)}"]`);
    if (!target) return;

    const checked = Array.from(
      this.form.querySelectorAll(`[data-multi-source="${CSS.escape(groupName)}"]:checked`)
    ).map((input) => input.value);

    target.value = checked.join(', ');
  }

  /** Liste lisible de tout ce qui a été choisi, affichée avant l'envoi. */
  collectSummary() {
    const entries = [];
    const seen = new Set();

    this.steps.slice(0, this.steps.length - 1).forEach((step) => {
      step.querySelectorAll('input, textarea, select').forEach((field) => {
        if (!field.name || field.type === 'hidden') return;
        if (field.type === 'file') {
          if (field.files?.length) entries.push([this.labelFor(field), field.files[0].name]);
          return;
        }
        if ((field.type === 'radio' || field.type === 'checkbox') && !field.checked) return;
        if (!field.value.trim()) return;

        const label = this.labelFor(field);
        const key = `${label}::${field.value}`;
        if (seen.has(key)) return;
        seen.add(key);
        entries.push([label, field.value]);
      });

      // Les groupes à choix multiple vivent dans un champ caché.
      step.querySelectorAll('[data-multi-target]').forEach((target) => {
        if (target.value.trim()) entries.push([target.dataset.multiTarget, target.value]);
      });
    });

    return entries;
  }

  /** Retrouve le libellé d'un champ à partir de son attribut name. */
  labelFor(field) {
    const match = field.name.match(/\[(.+)\]$/);
    return match ? match[1] : field.name;
  }

  buildRecap() {
    if (!this.recapList) return;

    const entries = this.collectSummary();
    this.recapList.innerHTML = '';

    if (entries.length === 0) {
      const empty = document.createElement('p');
      empty.className = 'kalara-config__hint';
      empty.textContent = 'Revenez aux étapes précédentes pour composer votre robe.';
      this.recapList.append(empty);
      return;
    }

    entries.forEach(([label, value]) => {
      const dt = document.createElement('dt');
      dt.textContent = label;
      const dd = document.createElement('dd');
      dd.textContent = value;
      this.recapList.append(dt, dd);
    });

    // Le formulaire de contact Shopify n'expédie l'e-mail que s'il reçoit un
    // corps de message : on y recopie le récapitulatif en texte brut.
    if (this.bodyTarget) {
      this.bodyTarget.value = entries.map(([label, value]) => `${label} : ${value}`).join('\n');
    }
  }

  onSubmit(event) {
    // Valider l'ensemble : une cliente peut revenir en arrière et vider un
    // champ déjà validé avant d'envoyer.
    for (let i = 0; i < this.steps.length; i += 1) {
      if (!this.validateStep(i)) {
        event.preventDefault();
        this.goTo(i);
        return;
      }
    }

    this.buildRecap();

    // Anti double-clic. Le verrou est posé au tour suivant : désactiver le
    // bouton dans le gestionnaire lui-même empêche certains navigateurs de
    // joindre sa valeur à la requête.
    if (this.submitButton) {
      window.setTimeout(() => {
        this.submitButton.setAttribute('aria-busy', 'true');
        this.submitButton.disabled = true;
      }, 0);
    }
  }
}

if (!customElements.get('kalara-configurator')) {
  customElements.define('kalara-configurator', KalaraConfigurator);
}
